import { useListCompetitors } from '@reelplicate/api-client/competitors';
import {
  getListMediaQueryKey,
  useDeleteMediaAssets,
  useListMedia,
  useTagMediaAssets,
  useUpdateMediaAsset,
} from '@reelplicate/api-client/media';
import {
  getListMediaCollectionsQueryKey,
  useAddMediaCollectionItems,
  useCopyMediaCollection,
  useCreateMediaCollection,
  useDeleteMediaCollection,
  useListMediaCollections,
  useRemoveMediaCollectionItem,
  useRemoveMediaCollectionItems,
  useUpdateMediaCollection,
} from '@reelplicate/api-client/media-collections';
import { MediaContentType, type MediaLibraryDto } from '@reelplicate/api-client/model';
import { useListPersonas } from '@reelplicate/api-client/personas';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import { toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useMemo, useRef, useState } from 'react';

import { makeFacets, makeRuleFacets } from './library';
import { BRAND_OWNER, toAsset, toCollection } from './media-api';
import { MediaContext, type MediaStore, type Upload } from './media-context';
import { applyTagChanges } from './tags';
import type { AssetKind, Owner } from './types';
import { ENTRIES_KEY } from './use-collection-entries';
import { isAbort, useMediaUpload } from './use-media-upload';

const ACCEPTED = new Set<string>(Object.values(MediaContentType));

function kindOf(file: File): AssetKind {
  if (file.type.startsWith('image/')) return 'image';
  if (file.type.startsWith('audio/')) return 'audio';
  return 'clip';
}

/**
 * The media library's data, from the API: assets, collections, the owners
 * (brand and personas) and storage, plus the upload queue. Uploads go
 * straight to storage with progress, then the API adds them to the library.
 */
export function MediaProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { data: workspaces } = useListWorkspaces();
  const workspace = workspaces?.[0];
  const id = workspace?.id ?? '';
  const enabled = { query: { enabled: Boolean(workspace) } };
  const { data: library } = useListMedia(id, enabled);
  const { data: collectionDtos } = useListMediaCollections(id, enabled);
  const { data: personas } = useListPersonas(id, enabled);
  const { data: competitors } = useListCompetitors(id, {
    query: { enabled: Boolean(workspace), throwOnError: false },
  });

  const updateAsset = useUpdateMediaAsset();
  const deleteAssets = useDeleteMediaAssets();
  const tagMediaAssets = useTagMediaAssets();
  const upload = useMediaUpload();
  const createMediaCollection = useCreateMediaCollection();
  const deleteMediaCollection = useDeleteMediaCollection();
  const updateMediaCollection = useUpdateMediaCollection();
  const copyMediaCollection = useCopyMediaCollection();
  const addItems = useAddMediaCollectionItems();
  const removeItem = useRemoveMediaCollectionItem();
  const removeItems = useRemoveMediaCollectionItems();

  const [uploads, setUploads] = useState<Upload[]>([]);
  // In-flight uploads' abort switches (aborting also cancels the half-made asset).
  const controllers = useRef(new Map<string, AbortController>());

  const owners = useMemo<Owner[]>(
    () => [
      { id: BRAND_OWNER, name: workspace?.name ?? 'Brand' },
      ...(personas?.items ?? []).map((p) => ({ id: p.id, name: p.name })),
    ],
    [workspace?.name, personas],
  );
  const facets = useMemo(() => makeFacets(owners), [owners]);
  const assets = useMemo(
    () => library?.items.map((dto) => toAsset(dto, owners)) ?? [],
    [library, owners],
  );
  const ruleFacets = useMemo(
    () =>
      makeRuleFacets(
        owners,
        competitors?.items ?? [],
        [...new Set(library?.items.flatMap((a) => a.tags) ?? [])].sort((a, b) =>
          a.localeCompare(b),
        ),
      ),
    [owners, competitors, library],
  );
  const collections = useMemo(
    () => collectionDtos?.map((dto) => toCollection(dto, ruleFacets)) ?? [],
    [collectionDtos, ruleFacets],
  );

  const mediaKey = getListMediaQueryKey(id);
  const collectionsKey = getListMediaCollectionsQueryKey(id);
  const refresh = (...keys: (typeof mediaKey | typeof collectionsKey)[]) =>
    Promise.all([
      ...keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })),
      // Smart collections' matches change with the library and with their rules.
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY, id] }),
    ]);

  /** Save an asset's tags: shown at once, put back if saving fails. */
  const saveTags = (assetId: string, next: (tags: string[]) => string[]) => {
    const before = queryClient.getQueryData<MediaLibraryDto>(mediaKey);
    const asset = before?.items.find((a) => a.id === assetId);
    if (!before || !asset) return;
    const tags = next(asset.tags);
    queryClient.setQueryData<MediaLibraryDto>(mediaKey, {
      ...before,
      items: before.items.map((a) => (a.id === assetId ? { ...a, tags } : a)),
    });
    updateAsset.mutate(
      { id, assetId, data: { tags } },
      {
        onError: () => queryClient.setQueryData(mediaKey, before),
        onSettled: () => void refresh(mediaKey),
      },
    );
  };

  const patchUpload = (uploadId: string, patch: Partial<Upload>) =>
    setUploads((all) => all.map((u) => (u.id === uploadId ? { ...u, ...patch } : u)));
  const dropUpload = (uploadId: string) =>
    setUploads((all) => all.filter((u) => u.id !== uploadId));

  /** One file, through the shared upload (start → PUT → poster → complete). */
  const run = async (uploadId: string, file: File, collectionId?: string) => {
    const controller = new AbortController();
    controllers.current.set(uploadId, controller);
    try {
      await upload(id, file, {
        signal: controller.signal,
        onProgress: (progress) => patchUpload(uploadId, { progress }),
        onStored: () => patchUpload(uploadId, { progress: 100, status: 'processing' }),
        collectionId,
      });
      patchUpload(uploadId, { status: 'complete' });
      await (collectionId ? refresh(mediaKey, collectionsKey) : refresh(mediaKey));
    } catch (error) {
      if (isAbort(error)) return; // cancelUpload tidies up.
      dropUpload(uploadId);
      // API failures already raised a toast; the storage PUT didn't.
      if (error instanceof Error && error.message.startsWith('Upload failed')) {
        toast.error(`${file.name} didn’t upload. Try again.`);
      }
    } finally {
      controllers.current.delete(uploadId);
    }
  };

  const store: MediaStore = {
    workspaceId: id,
    loading: !library || !collectionDtos,
    assets,
    collections,
    owners,
    facets,
    ruleFacets,
    storage: {
      usedBytes: library?.storageUsedBytes ?? 0,
      limitBytes: library?.storageLimitBytes ?? 0,
    },
    uploads,
    addToCollection: (collectionId, assetIds) =>
      addItems.mutate(
        { id, collectionId, data: { assetIds } },
        { onSuccess: () => void refresh(collectionsKey, mediaKey) },
      ),
    removeFromCollection: (collectionId, assetId) =>
      removeItem.mutate(
        { id, collectionId, assetId },
        { onSuccess: () => void refresh(collectionsKey, mediaKey) },
      ),
    removeManyFromCollection: (collectionId, assetIds) =>
      removeItems.mutate(
        { id, collectionId, data: { assetIds } },
        { onSuccess: () => void refresh(collectionsKey, mediaKey) },
      ),
    createCollection: async (name, { assetIds, rule }) => {
      try {
        const created = await createMediaCollection.mutateAsync({
          id,
          data: { name, assetIds, rule },
        });
        await refresh(collectionsKey, mediaKey);
        return toCollection(created, ruleFacets);
      } catch {
        return undefined; // The failure is already shown as a toast.
      }
    },
    updateCollection: async (collectionId, { name, rule }) => {
      try {
        const updated = await updateMediaCollection.mutateAsync({
          id,
          collectionId,
          data: { name, rule },
        });
        await refresh(collectionsKey);
        return toCollection(updated, ruleFacets);
      } catch {
        return undefined; // The failure is already shown as a toast.
      }
    },
    copyCollection: async (collectionId) => {
      try {
        const copied = await copyMediaCollection.mutateAsync({ id, collectionId });
        await refresh(collectionsKey, mediaKey);
        return toCollection(copied, ruleFacets);
      } catch {
        return undefined;
      }
    },
    deleteCollection: async (collectionId) => {
      try {
        await deleteMediaCollection.mutateAsync({ id, collectionId });
      } catch {
        return false;
      }
      await refresh(collectionsKey, mediaKey);
      return true;
    },
    addTag: (assetId, tag) =>
      saveTags(assetId, (tags) =>
        tags.some((t) => t.toLowerCase() === tag.toLowerCase()) ? tags : [...tags, tag],
      ),
    removeTag: (assetId, tag) =>
      saveTags(assetId, (tags) => tags.filter((t) => t !== tag)),
    tagAssets: (assetIds, changes) => {
      // Shown at once, put back if saving fails - as saveTags does for one.
      const before = queryClient.getQueryData<MediaLibraryDto>(mediaKey);
      const picked = new Set(assetIds);
      if (before) {
        queryClient.setQueryData<MediaLibraryDto>(mediaKey, {
          ...before,
          items: before.items.map((a) =>
            picked.has(a.id) ? { ...a, tags: applyTagChanges(a.tags, changes) } : a,
          ),
        });
      }
      tagMediaAssets.mutate(
        { id, data: { assetIds, ...changes } },
        {
          onError: () => before && queryClient.setQueryData(mediaKey, before),
          onSettled: () => void refresh(mediaKey),
        },
      );
    },
    trash: (assetIds) =>
      deleteAssets.mutate(
        { id, data: { assetIds } },
        { onSuccess: () => void refresh(mediaKey, collectionsKey) },
      ),
    upload: (files, { collectionId } = {}) => {
      const accepted = files.filter((file) => {
        if (ACCEPTED.has(file.type)) return true;
        toast.error(
          `${file.name} can’t be uploaded. Use MP4, MOV, WebM, PNG, JPG, WebP, GIF, MP3, M4A or WAV.`,
        );
        return false;
      });
      const queued = accepted.map((file, i) => ({
        file,
        upload: {
          id: `${Date.now().toString(36)}-${i}`,
          name: file.name,
          size: file.size,
          progress: 0,
          status: 'uploading' as const,
          kind: kindOf(file),
          preview:
            file.type.startsWith('image/') && typeof URL.createObjectURL === 'function'
              ? URL.createObjectURL(file)
              : undefined,
        },
      }));
      setUploads((prev) => [
        ...prev.filter((u) => u.status !== 'complete'),
        ...queued.map((q) => q.upload),
      ]);
      for (const { file, upload } of queued) void run(upload.id, file, collectionId);
    },
    cancelUpload: (uploadId) => {
      controllers.current.get(uploadId)?.abort();
      dropUpload(uploadId);
    },
    // Finished uploads go; ones still running stay in the queue.
    dismissUploads: () => setUploads((all) => all.filter((u) => u.status !== 'complete')),
  };

  return <MediaContext value={store}>{children}</MediaContext>;
}
